'use client'

import { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { cn } from '@/lib/utils'
import {Input} from "@/components/ui/input";

const periodNames = {
  quarterly: 'quarterly',
  halfYearly: 'half yearly',
  annual: 'annually',
  lifetime: 'once',
}


const currencies = {
  USD: { symbol: '$', name: "USD", rate: 1 / 85 },
  EUR: { symbol: '€', name: "EUR", rate: 0.85 / 85 },
  GBP: { symbol: '£', name: "GBP", rate: 0.73 / 85 },
  INR: { symbol: '₹', name: "INR", rate: 1 },
}

export default function PfPricingPageComponent() {
  const [selectedPeriod, setSelectedPeriod] = useState('annual');
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [userLicenses, setUserLicenses] = useState<number>(100);
  const [selectedCampaigns, setSelectedCampaigns] = useState(['fieldCampaign', 'digitalCampaign', "channelCampaign"]); // Initially selected campaigns
  const [selectedAdvancedFeatures, setSelectedAdvancedFeatures] = useState<string[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isPando, setIsPando] = useState(false);


  const features = {
    fieldCampaign: {
      price: {
        quarterly: 660, // 495 + 165
        halfYearly: 600, // 450 + 150
        annual: 500, // 375 + 125
        lifetime: 12000, // 9000 + 3000
      },
      name: "Field Campaign",
      description: "Direct engagement strategies for face-to-face interactions and community outreach.",
      featureList: [
        { id: 'manageMarkets', name: 'Manage Markets' },
        { id: 'setTargetsFOActivities', name: 'Activity Target Setting' },
        { id: 'trackManageFieldActivities', name: 'Monitor Sales Activities' },
        { id: 'segmentManageFarmers', name: "Segment & Manage Customers" },
        { id: 'trackManageProductDemos', name: 'Run Product Demos' },
        { id: 'monitorRetailerActivities', name: 'Monitor Sales Activities' },
        { id: 'manageProductLiquidation', name: 'Manage Product Liquidation' },
        { id: 'salesTargetSetting', name: 'Sales Target Setting' }
      ]
    },
    // channelCampaign: {
    //   price: {
    //     quarterly: 165,
    //     halfYearly: 150,
    //     annual: 125,
    //     lifetime: 3000,
    //   },
    //   name: "Channel Campaign",
    //   description: "Traditional media strategies to broadcast your message through established communication channels.",
    //   featureList: [
    //     { id: 'monitorRetailerActivities', name:isPando ? 'Monitor Sales Activities' : 'Monitor Retailer Activities' },
    //     { id: 'manageProductLiquidation', name: 'Manage Product Liquidation' },
    //     { id: 'salesTargetSetting', name: 'Sales Target Setting' }
    //   ]
    // },
    digitalCampaign: {
      price: {
        quarterly: 396,
        halfYearly: 360,
        annual: 300,
        lifetime: 0,
      },
      name: "Digital Campaign",
      description: "Online marketing tactics to reach and engage your audience through various digital platforms.",
      featureList: [
        { id: 'publishManageSocialMediaContent', name: 'Manage Social Media' },
        { id: 'hireInfluencersPromoReels', name: 'Run Influencer Marketing' },
        { id: 'publishContent3rdPartyChannels',   name: "Manage Customer Leads" },
        { id: 'manageFarmerLeads', name: "Direct Customer Chat" },
        { id: 'farmerChatCallServiceCRM', name: 'Run Targeted Campaign' },
        // { id: 'acceptManageFarmerServiceDelivery', name: 'Accept/manage farmer service delivery' }
      ]
    }
  };
  
  const advancedFeatures = {
    bulkMessaging: {
      id: 'bulkMessaging',
      name: 'Add On Features',
      description: 'Send bulk messages to reach a wider audience efficiently.',
      featureList: [
        {
          id: 'ekycOnboarding',
          price: 25,
          name: 'E-KYC Onboarding',
          description: 'Streamline the onboarding process with digital KYC capabilities.'
        },
        {
          id: 'missedCallFarmerRegistration',
          price: 50,
          name: 'Farmer Missed Call Registration',
          description: 'Enable farmer registrations through simple missed calls.'
        },
        {
          id: 'imageScanningRegistration',
          price: 50,
          name: 'Farmer Data OCR',
          description: 'Register farmers using advanced image scanning technology.'
        },
        {
          id: 'highAccuracyLocationMapping',
          price: 125,
          name: 'High Accuracy Location Mapping',
          description: 'Provide precise location mapping for field activities.'
        },
        {
          id: 'mobileTraining',
          price: 125,
          name: 'Mobile Training',
          description: 'Offer mobile-based training for field officers and farmers.'
        },
        {
          id: 'FieldOfficerAssistCo-PIlot',
          price: 125,
          name: 'Field Officer Assist Co-Pilot',
          description: 'Offer mobile-based training for field officers and farmers.'
        },
        {
          id: 'incentiveEstimation',
          price: 20,
          name: 'Incentive Estimation',
          description: 'Estimate incentives accurately for field officers.'
        }
      ]
    },
  }
  
  
  useEffect(() => {
    if (selectedPeriod === 'lifetime') {
      setUserLicenses(300);
    }
  }, [selectedPeriod]);

  const toggleCampaign = (campaignType: string) => {
    if (campaignType === 'fieldCampaign') {
      setSelectedCampaigns(prev => {
        if (prev.includes(campaignType)) {
          setSelectedAdvancedFeatures([]);
          return prev.filter(type => type !== campaignType);
        } else {
          return [...prev, campaignType];
        }
      });
    } else {
      setSelectedCampaigns(prev =>
        prev.includes(campaignType)
          ? prev.filter(type => type !== campaignType)
          : [...prev, campaignType]
      );
    }
  };

  const toggleAdvancedFeature = (featureId: string) => {
    setSelectedAdvancedFeatures(prev => {
      const newSelection = prev.includes(featureId)
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId];

      // Automatically select "Field Campaign" if any advanced feature is selected
      if (!prev.includes(featureId) && !selectedCampaigns.includes('fieldCampaign')) {
        return newSelection; // Returning the new selection
      }

      return newSelection;
    });

    // Ensure Field Campaign is selected if any advanced feature is selected
    if (!selectedCampaigns.includes('fieldCampaign')) {
      setSelectedCampaigns(prev => [...prev, 'fieldCampaign']);
    }
  };
  const calculatePrice = (basePrice: any, isAddon: boolean = false) => {
    // console.log({basePrice});

    const periodPrice = typeof basePrice === 'number' ? basePrice : basePrice[selectedPeriod];
    let licenseMultiplier = 1;
    // switch (userLicenses) {
    //   case "<100": licenseMultiplier = 1; break;
    //   case "101-250": licenseMultiplier = isAddon ? 2.5 : 2.375; break;
    //   case "251-500": licenseMultiplier = isAddon ? 5 : 4.5; break;
    //   case "501-1000": licenseMultiplier = isAddon ? 10 : 8.5; break;
    //   case ">1000": licenseMultiplier = isAddon ? 20 : 16; break;
    // }
    const totalPriceInINR = periodPrice * licenseMultiplier
    // console.log({periodPrice, basePrice, isAddon, selectedPeriod});
    return totalPriceInINR * currencies[selectedCurrency as keyof typeof currencies].rate
  }

  useEffect(() => {
    const basePrice = Object.entries(features)
      .filter(([campaignType]) => selectedCampaigns.includes(campaignType))
      .reduce((total, [, campaignFeatures]) => total + calculatePrice(campaignFeatures.price), 0)

    const advancedFeaturesPrice = Object.values(advancedFeatures)
      .flatMap(feature => feature.featureList)
      .filter(feature => selectedAdvancedFeatures.includes(feature.id))
      .reduce((total, feature) => total + feature.price, 0)

    const totalPriceInINR = (basePrice + advancedFeaturesPrice)

    const periodMultiplier = {
      quarterly: 3,
      halfYearly: 6,
      annual: 12,
      lifetime: 1,
    }
    // console.log({basePrice, totalPriceInINR, userLicenses, periodMultiplier: periodMultiplier[selectedPeriod]});
    setTotalPrice(totalPriceInINR * userLicenses * periodMultiplier[selectedPeriod])
  }, [selectedCampaigns, selectedAdvancedFeatures, selectedPeriod, userLicenses, selectedCurrency]);

  useEffect(() => {
    const domain = window?.location?.hostname || "";

    if (domain.includes("pando")) {
      setIsPando(true);
    }
  }, []);

  const discountsbasedOnlicense = (userLicenses_: number) => {
      // if ( userLicenses_ < 100 ) return 0;
      // if ( userLicenses_ < 500 ) return 5;
      // if ( userLicenses_ < 1000 ) return 10;
      // if ( userLicenses_ < 1500 ) return 15;
      // if ( userLicenses_ < 2000 ) return 20;
      return 0;
  }

  const renderFeatures = (campaignType: keyof typeof features, period) => {
    const campaign = features[campaignType];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Switch
              id={campaignType}
              checked={selectedCampaigns.includes(campaignType)}
              onCheckedChange={() => toggleCampaign(campaignType)}
              disabled={period.id === 'lifetime' && campaignType === 'digitalCampaign'}
              className={cn(
                "data-[state=checked]:bg-primary",
                "data-[state=unchecked]:bg-[#D4D4D4]"
              )}
            />
            <Label htmlFor={campaignType} className="flex-1 font-semibold">
              {campaign.name}
            </Label>
          </div>
          {period.id === 'lifetime' && campaignType === 'digitalCampaign' ? <span className={`text-md font-bold text-primary line-through decoration-2`}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: selectedCurrency,
              minimumFractionDigits: 0
            }).format(7200)} {'/ user'}
          </span> :
          <span className={`text-md font-bold ${selectedCampaigns.includes(campaignType) ? 'text-primary' : 'text-[black]'}`}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: selectedCurrency,
              minimumFractionDigits: 0
            }).format(calculatePrice(campaign.price, false))} {period.id === 'lifetime' ? '/ user' : '/ user / month'}
          </span> }
        </div>
        {campaign.featureList.map(feature => (
          <div key={feature.id} className="flex items-center justify-between pl-8">
            <Label htmlFor={feature.id} className={`flex-1 ${selectedCampaigns.includes(campaignType) ? 'text-primary' : 'text-muted-foreground'}`}>
              {feature.name}
            </Label>
          </div>
        ))}
      </div>
    );
  };


  const renderAdvancedFeatures = () => {
    return Object.entries(advancedFeatures).map(([, feature]) => (
      <div key={feature.id} className="space-y-4">
        <h3 className="font-semibold">{feature.name}</h3>
        {feature.featureList.map(subFeature => (
          <div key={subFeature.id}   className="pl-4 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">

            <div className="flex items-start sm:items-center space-x-3">
              <Switch
                id={subFeature.id}
                checked={selectedAdvancedFeatures.includes(subFeature.id)}
                onCheckedChange={() => toggleAdvancedFeature(subFeature.id)}
                className={cn(
                  "data-[state=checked]:bg-primary",
                  "data-[state=unchecked]:bg-[#D4D4D4]"
                )}
              />
                  <div className={`flex flex-col ${selectedAdvancedFeatures.includes(subFeature.id) ? 'text-primary' : 'text-[black]'}`}>

                  <Label htmlFor={subFeature.id} className="text-sm font-medium">

                {subFeature.name}
              </Label>

              <span className="text-sm font-bold sm:hidden">
              {/* {currencies[selectedCurrency as keyof typeof currencies].symbol} */}

              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: selectedCurrency,
                minimumFractionDigits: 0
              }).format(calculatePrice(subFeature.price, true))}&nbsp;/ user / month
            </span>
            </div>
          </div>
          {/* Right Side: Users on larger screens only */}
          <span
               className={`text-sm font-bold hidden sm:block ${
               selectedAdvancedFeatures.includes(subFeature.id)
                ? "text-primary"
               : "text-[black]"
               }`}
            >
           {new Intl.NumberFormat("en-IN", {
           style: "currency",
          currency: selectedCurrency,
          minimumFractionDigits: 0,
           }).format(calculatePrice(subFeature.price, true))}{" "}
           / user / month
          </span>
          </div>
        ))}
      </div>
    ))
  }

  return (
    <div className="container mx-auto p-4 space-y-8  pt-[100px]">
      <div>
        <h1 className="text-3xl font-bold text-center">Flexible Pricing for every business</h1>
        {/* <p className='text-center mt-0'>Get Started with a 3 Months Free Trial</p> */}
      </div>
      <div className="flex justify-center space-x-4 mb-8">


      </div>

      <div className="flex flex-col sm:flex-row justify-center sm:space-x-4 space-y-4 sm:space-y-0">

        <div >
          <div className="w-full sm:w-[600px]">

            <Tabs defaultValue="annual" onValueChange={setSelectedPeriod}>
            <div className="flex flex-col sm:flex-row justify-between gap-4">

                <TabsList className="inline-flex mb-4">
                  <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
                  <TabsTrigger value="halfYearly">Half Yearly </TabsTrigger>
                  <TabsTrigger value="annual">Annual</TabsTrigger>
                  {/*<TabsTrigger value="lifetime">Lifetime</TabsTrigger>*/}
                </TabsList>
                <div className="w-40 my-2 sm:my-0">


                  {/* <Label htmlFor="currency">Currency</Label> */}
                  <Select onValueChange={setSelectedCurrency} defaultValue={selectedCurrency}>
                    <SelectTrigger id="currency">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(currencies).map((currency) => (
                        <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {[
                { id: 'quarterly', name: 'Quarterly' },
                { id: 'halfYearly', name: 'Half Yearly' },
                { id: 'annual', name: 'Annual' },
                { id: 'lifetime', name: 'Lifetime' }
              ].map((period) => (
                <TabsContent key={period.id} value={period.id}>
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {period.name} Pricing
                        {period.id !== 'lifetime' && period.id !=='quarterly' && <span className="text-primary ml-2 bg-accent px-2 py-1 rounded-md text-sm">Upto&nbsp;
                          {period.id === "halfYearly"
                            ? `${10 + discountsbasedOnlicense(userLicenses)}% OFF`
                            : period.id === "annual"
                              ? `${25 + discountsbasedOnlicense(userLicenses)}% OFF`
                              : discountsbasedOnlicense(userLicenses)
                                ? `${discountsbasedOnlicense(userLicenses)}% OFF`
                                : null}
                        </span>}
                        {period.id === 'lifetime' && <span className="text-primary ml-2 bg-accent px-2 py-1 rounded-md text-sm">Save 40%</span>}
                      </CardTitle>
                      <CardDescription>Select features for your campaign</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                      {renderFeatures('digitalCampaign', period)}
                      <Separator className="my-4" />
                      {renderFeatures('fieldCampaign', period)}
                      {/* <Separator className="my-4" />
                      {renderFeatures('channelCampaign', period)} */}
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}

              {selectedPeriod !== 'lifetime' && <>
              {!isPando && <Card className='mt-4'>
                <CardHeader>
                </CardHeader>
                <CardContent className="space-y-8">
                  {renderAdvancedFeatures()}
                </CardContent>
              </Card>}
              </>}
            </Tabs>
          </div>

          <div className="w-full sm:w-[600px] bg-background border border-border p-4 shadow-sm rounded-sm mt-4 h-fit">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">

              <div className='flex flex-row items-center '>

                <div className="w-45">
                  {/* <Label htmlFor="licenses">User Licenses</Label> */}
                  {/*<Select onValueChange={setUserLicenses} defaultValue="<100">*/}
                  {/*  <SelectTrigger id="licenses">*/}
                  {/*    <SelectValue placeholder="Select licenses" />*/}
                  {/*  </SelectTrigger>*/}
                  {/*  <SelectContent>*/}
                  {/*    <SelectItem value="<100">100 users</SelectItem>*/}
                  {/*    <SelectItem value="101-250">101-250 users  <span className='text-green-500 bg-accent px-[4px] py-[2px] rounded-md text-[8px]'>  5% OFF</span></SelectItem>*/}
                  {/*    <SelectItem value="251-500">251-500 users  <span className='text-green-500 bg-accent px-[4px] py-[2px] rounded-md text-[8px]'>  10% OFF</span></SelectItem>*/}
                  {/*    <SelectItem value="501-1000">501-1000 users  <span className='text-green-500 bg-accent px-[4px] py-[2px] rounded-md text-[8px]'>  15% OFF</span></SelectItem>*/}
                  {/*    <SelectItem value=">1000">&gt;1000 users  <span className='text-green-500 bg-accent px-[4px] py-[2px] rounded-md text-[8px]'>  20% OFF</span></SelectItem>*/}
                  {/*  </SelectContent>*/}
                  {/*</Select>*/}
                  <span>User Licenses </span>
                  <Input value={userLicenses} type={"number"} onChange={e => setUserLicenses(+e.target.value)} />
                </div>
              </div>
              <div className="text-3xl font-bold tabular-nums">
                {/*<span className="text-lg font-semibold">Total Price:</span>*/}
                <span className="inline-block w-auto text-right">
                  {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: selectedCurrency,
                    minimumFractionDigits: 0
                  }).format(totalPrice)}
                </span><br/>
                <div className="font-normal text-xs">&nbsp;billed {periodNames[selectedPeriod]} {selectedPeriod === 'lifetime' ? ' + 25% AMC' : ''}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
